import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { PickSession } from "@redotech/redo-model/wms/schema/pick-session";
import { createContext, memo, ReactNode, useState } from "react";
import { useWatchStream } from "../../hooks/use-watch-stream";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";

interface PickSessionContextValue {
  pickSession: PickSession | undefined;
  fulfillmentGroupIds: string[];
  fulfillmentGroups: Record<string, FulfillmentGroupData>;
  allFulfillmentGroupsLoaded: boolean;
  pendingFulfillmentGroups: boolean;
  setPickSession: (session: PickSession) => void;
  updatePickSession: (session: PickSession) => void;
  clearPickSession: () => void;
  setFulfillmentGroups: (groups: FulfillmentGroupData[]) => void;
}

export const PickSessionContext = createContext<
  PickSessionContextValue | undefined
>(undefined);

export const PickSessionProvider = memo(function PickSessionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const [pickSession, setPickSessionState] = useState<
    PickSession | undefined
  >();
  const [fulfillmentGroups, setFulfillmentGroupsState] = useState<
    Record<string, FulfillmentGroupData>
  >({});
  const [fulfillmentGroupIds, setFulfillmentGroupIds] = useState<string[]>([]);

  const pendingFulfillmentGroups = useWatchStream(
    client,
    "watchFulfillmentGroups",
    { fulfillmentGroupIds },
    ({ fulfillmentGroups }) => {
      setFulfillmentGroups(fulfillmentGroups);
    },
    true,
  );

  const allFulfillmentGroupsLoaded = fulfillmentGroupIds.every(
    (id) => fulfillmentGroups[id],
  );

  const setPickSession = useHandler((session: PickSession) => {
    setPickSessionState(session);
    const groupIds = Array.from(
      new Set(session.items.map((item) => item.fulfillmentGroupId)),
    );
    setFulfillmentGroupIds(groupIds);
  });

  const updatePickSession = useHandler((session: PickSession) => {
    setPickSessionState(session);
    const groupIds = Array.from(
      new Set(session.items.map((item) => item.fulfillmentGroupId)),
    );
    setFulfillmentGroupIds(groupIds);

    // Remove fulfillment groups that are no longer in the pick session
    setFulfillmentGroupsState((prevGroups) => {
      const newFulfillmentGroups: Record<string, FulfillmentGroupData> = {};
      for (const id of groupIds) {
        if (prevGroups[id]) {
          newFulfillmentGroups[id] = prevGroups[id];
        }
      }
      return newFulfillmentGroups;
    });
  });

  const clearPickSession = useHandler(() => {
    setPickSessionState(undefined);
    setFulfillmentGroups([]);
    setFulfillmentGroupsState({});
    setFulfillmentGroupIds([]);
  });

  const setFulfillmentGroups = useHandler((groups: FulfillmentGroupData[]) => {
    setFulfillmentGroupsState((prevGroups) => {
      const newGroups = { ...prevGroups };
      for (const group of groups) {
        if (fulfillmentGroupIds.includes(group._id)) {
          newGroups[group._id] = group;
        }
      }
      return newGroups;
    });
  });

  const value: PickSessionContextValue = {
    pickSession,
    fulfillmentGroupIds,
    fulfillmentGroups,
    allFulfillmentGroupsLoaded,
    pendingFulfillmentGroups,
    setPickSession,
    updatePickSession,
    clearPickSession,
    setFulfillmentGroups,
  };

  return (
    <PickSessionContext.Provider value={value}>
      {children}
    </PickSessionContext.Provider>
  );
});
